/* =========================================================
   VFW Header — mega-nav interactions (2026-07-24)
   · Dropdown/mega panels: hover is CSS; this adds click + keyboard
     toggling, one-open-at-a-time, Escape + outside-click to close.
   · Mobile drawer open/close.
   · Motion: condense on scroll; hide on scroll-down, reveal on
     scroll-up (reads window AND .page-wrapper per Horizon 4.x).
     Disabled under prefers-reduced-motion (header stays put).
   No JS: links work, hover/focus reveals panels, drawer uses
   native <details> accordions.
   ========================================================= */

(() => {
  const header = document.querySelector('[data-vfw-header]');
  if (!header) return;

  const items = Array.from(header.querySelectorAll('[data-nav-item]'));

  function closeItem(item) {
    item.classList.remove('is-open');
    const t = item.querySelector('[data-nav-toggle]');
    if (t) t.setAttribute('aria-expanded', 'false');
  }
  function closeAll(except) {
    items.forEach((it) => {
      if (it !== except) closeItem(it);
    });
  }

  // .is-dismissed (set by Escape and by click-to-close) outranks both :hover
  // and :focus-within, and used to be cleared ONLY by mouseleave. A
  // keyboard-only user who pressed Escape and then Tabbed back to the trigger
  // could therefore never reopen the panel — it was unreachable without
  // touching a mouse. Focus arriving is a fresh intent to open, so clear it.
  items.forEach((item) => {
    item.addEventListener('focusin', () => item.classList.remove('is-dismissed'));
  });

  // ── Click / keyboard toggles (hover handled by CSS) ──
  items.forEach((item) => {
    const toggle = item.querySelector('[data-nav-toggle]');
    if (!toggle) return;
    toggle.addEventListener('click', (e) => {
      e.preventDefault();
      const willOpen = !item.classList.contains('is-open');
      closeAll(item);
      item.classList.toggle('is-open', willOpen);
      // Closing by click used to do nothing visible: the pointer is still on
      // the trigger, so the :hover rule re-revealed the panel the instant
      // .is-open came off. .is-dismissed outranks :hover (and :focus-within)
      // until the pointer leaves, which is the same mechanism Escape uses.
      item.classList.toggle('is-dismissed', !willOpen);
      toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    });
  });

  // ── Hover intent (James, 2026-07-25) ──
  // CSS :hover alone cannot carry the pointer from the trigger into the panel.
  // The panel is anchored to the header (so it can't run off-screen), which
  // means the natural diagonal from "Shop Wheels" down to a rail row leaves the
  // <li> sideways — through a neighbouring nav item — before it reaches the
  // panel. :hover drops there and the panel closes. Only an unrealistically
  // fast pointer skips that band, which is why it looked intermittent.
  //
  // A short close DELAY fixes every version of this (vertical gap, diagonal
  // travel, overshoot) without geometry hacks. The panel is a descendant of the
  // item, so re-entering it fires mouseenter again and cancels the pending
  // close. .is-open is the same flag click/keyboard already use, so CSS needs
  // no change and the no-JS hover behaviour is untouched.
  // The delay only has to bridge the trigger→panel hand-off, and the geometry
  // already does most of that work (full-height <li> + the panel's 10px
  // padding-top bridge). 260ms stacked on top of the 220ms CSS fade read as
  // ~half a second of hang — James, 2026-07-27: "it'll still be persistent a
  // little bit longer than it should be." 140ms bridges the gap and reads
  // instant. The CSS close transition was shortened to match.
  const CLOSE_DELAY = 140;

  // Scroll position captured when a panel opens — see the scroll handler.
  // Declared here because both the hover block and onScroll need it.
  let openedAtY = 0;
  // Scrolling the PAGE means the nav is finished with, so an open panel should
  // go. But a trackpad emits a few pixels from a resting palm, and closing on
  // that would make the menu feel twitchy. 40px is past any accidental nudge
  // and well short of a deliberate scroll.
  const SCROLL_DISMISS = 40;

  // Leaving the item is ambiguous (you may be travelling to the panel).
  // Leaving the whole HEADER is not — close immediately, no delay. mouseleave
  // counts descendants, and the panels are descendants, so this only fires
  // once the pointer is off the trigger AND off the panel.
  function closeNow() {
    const timers = header.__vfwCloseTimers;
    if (timers) timers.forEach((clear) => clear());
    // Must release focus itself, NOT rely on the per-item timers: it just
    // cancelled them. Leaving this out was a real bug — the browser test
    // caught the panel still pinned open by :focus-within after a click.
    items.forEach(releaseClickFocus);
    closeAll(null);
  }

  if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
    const cancels = [];
    header.__vfwCloseTimers = cancels;
    items.forEach((item) => {
      if (!item.querySelector('[data-nav-panel]')) return;
      let timer = null;
      const cancel = () => { if (timer) { clearTimeout(timer); timer = null; } };
      cancels.push(cancel);
      item.addEventListener('mouseenter', () => {
        cancel();
        // A POINTER ARRIVING IS A FRESH INTENT TO OPEN — same as focus arriving
        // (see the focusin handler above), and .is-dismissed must yield to it.
        // Without this: press Escape while the pointer is somewhere else, then
        // reach for the mouse and hover the trigger. mouseenter set .is-open,
        // but .is-dismissed — set by Escape and only ever cleared by mouseleave
        // or focusin — outranked it in CSS. Measured live 2026-08-04:
        // {open:true, dismissed:true, visibility:hidden} with aria-expanded
        // "true". A dead menu that also lied to screen readers. Click-to-close
        // is unaffected: the pointer never leaves the trigger, so mouseenter
        // does not re-fire and the panel stays shut as intended.
        item.classList.remove('is-dismissed');
        closeAll(item);
        item.classList.add('is-open');
        openedAtY = getY();
        const t = item.querySelector('[data-nav-toggle]');
        if (t) t.setAttribute('aria-expanded', 'true');
      });
      item.addEventListener('mouseleave', () => {
        cancel();
        item.classList.remove('is-dismissed');
        timer = setTimeout(() => {
          closeItem(item);
          releaseClickFocus(item);
          timer = null;
        }, CLOSE_DELAY);
      });
    });
    header.addEventListener('mouseleave', closeNow);
    // Hand-off flag, set LAST — only once every hover handler above is bound.
    // It switches off the CSS :hover reveal (vfw-header.css) so .is-open is the
    // only thing that opens a panel. Two authorities that could disagree were
    // the whole cause of the panel that would not go away; if this line is
    // never reached (JS error above, JS disabled), the CSS rule stays live and
    // the menu still works on hover.
    document.documentElement.classList.add('vfw-nav-js');
  }

  // ── The panel that would not go away (James, 2026-07-27) ──
  // CSS opens a panel on :hover OR :focus-within. Clicking a top-level trigger
  // with the mouse leaves focus ON that trigger, so :focus-within stays true
  // and pins the panel open — pointer position no longer matters, and neither
  // does .is-open. That is the "sometimes I can't get it to go away" case, and
  // no amount of close-delay tuning touches it.
  //
  // Fix: when a pointer-driven close runs, drop focus if it is sitting on the
  // TRIGGER (the click artefact). Focus deeper inside the panel is left alone —
  // that is a keyboard user genuinely navigating, and :focus-within holding the
  // panel open for them is correct behaviour, not a bug.
  function releaseClickFocus(item) {
    const t = item.querySelector('[data-nav-toggle]');
    if (!t || document.activeElement !== t) return;
    // :focus-visible is precisely the "did this focus come from the keyboard"
    // signal, so it separates a keyboard user who tabbed here (leave them
    // alone — the panel SHOULD stay open for them) from the click artefact we
    // are clearing. Without this guard, a mouse merely sweeping across the
    // header would steal a keyboard user's focus.
    try {
      if (t.matches(':focus-visible')) return;
    } catch (err) {
      /* engine without :focus-visible — fall through and blur */
    }
    t.blur();
  }

  // Escape closes the open panel and returns focus to its trigger.
  // Listens on the DOCUMENT, not the header: a panel opened by hover has no
  // focus inside the header, so a header-scoped listener never fired and the
  // panel could not be dismissed from the keyboard — WCAG 2.2 SC 1.4.13
  // (Content on Hover or Focus) requires hover-revealed content be dismissible
  // without moving the pointer. Focus only returns to the trigger when focus
  // was already inside the header, so a hover-dismiss doesn't yank it.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    // Find the panel that is ACTUALLY ON SCREEN, not the one flagged .is-open.
    // Three different rules can reveal a panel (:hover, :focus-within,
    // .is-open) and they disagree: after a scroll-close the pointer is still
    // over the trigger, so the panel is visible with .is-open already false —
    // and the old .is-open lookup returned null and Escape did nothing. That
    // is a WCAG 2.2 SC 1.4.13 failure: hover-revealed content must be
    // dismissible. Reading computed style dismisses whatever is really up.
    const open = items.find((it) => {
      const p = it.querySelector('[data-nav-panel]');
      if (!p) return false;
      const cs = getComputedStyle(p);
      return cs.visibility === 'visible' && parseFloat(cs.opacity) > 0.05;
    });
    if (!open) return;
    const focusWasInside = header.contains(document.activeElement);
    const t = open.querySelector('[data-nav-toggle]');
    closeItem(open);
    // ORDER MATTERS. Return focus FIRST, then set the dismiss flag: moving
    // focus out of the panel back to the trigger fires focusin, and the
    // focusin handler above clears .is-dismissed. Setting the flag first
    // would have it wiped a tick later and the panel would spring back open —
    // Escape would visibly fail for exactly the keyboard users it exists for.
    if (t && focusWasInside) t.focus();
    // Clearing .is-open is not enough on its own: the pointer is usually still
    // over the trigger, and the CSS :hover rule would immediately re-reveal the
    // panel — so it would not actually be dismissed. .is-dismissed outranks the
    // hover and focus rules until the pointer leaves or focus arrives afresh.
    open.classList.add('is-dismissed');
  });

  // Click outside the header closes everything
  document.addEventListener('click', (e) => {
    if (!header.contains(e.target)) closeAll(null);
  });

  // ── Mobile drawer ──
  // Full-height takeover: the drawer is fixed from the header's real bottom
  // edge to the viewport bottom. The old version hardcoded a 62px offset,
  // which the announcement bar (taller, and variable when its text wraps)
  // made wrong — the drawer's last rows could land beyond the viewport with
  // no way to scroll them into view. Reading the live rect is always right,
  // whatever the bar or condensed state is doing.
  const drawerBtn = header.querySelector('[data-drawer-toggle]');
  const drawer = document.getElementById('vfw-nav-drawer');
  function placeDrawer() {
    if (!drawer) return;
    // Height to the viewport bottom from the header's real bottom edge —
    // the announcement bar and condensed state are both in the rect.
    const top = header.getBoundingClientRect().bottom;
    drawer.style.setProperty('--vfw-drawer-h', `calc(100dvh - ${top}px)`);
  }
  function setDrawer(open) {
    if (!drawer || !drawerBtn) return;
    if (open) placeDrawer();
    drawer.classList.toggle('is-open', open);
    drawerBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    // Lock the page behind the drawer (4.x scrolls .page-wrapper on
    // desktop and body on phones — the class covers both).
    document.documentElement.classList.toggle('vfw-drawer-lock', open);
  }
  if (drawerBtn && drawer) {
    drawerBtn.addEventListener('click', () => {
      setDrawer(!drawer.classList.contains('is-open'));
    });
    drawer.addEventListener('click', (e) => {
      if (e.target.closest('a')) setDrawer(false);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        setDrawer(false);
        drawerBtn.focus();
      }
    });
    // Keep the top edge honest through rotation / viewport resizes; leaving
    // mobile entirely (desktop nav reappears) closes and unlocks.
    window.addEventListener('resize', () => {
      if (!drawer.classList.contains('is-open')) return;
      if (window.matchMedia('(min-width: 1200px)').matches) {
        setDrawer(false);
      } else {
        placeDrawer();
      }
    }, { passive: true });
  }

  // ── Scroll: slide under the announcement bar, condense, hide/reveal ──
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scroller = document.querySelector('.page-wrapper');
  const getY = () => Math.max(window.scrollY || 0, scroller ? scroller.scrollTop : 0);

  // The header is fixed; the announcement bar sits above it in normal flow.
  // Slide the header down by whatever height of the bar is still on screen,
  // so it pins to top:0 only once the bar has scrolled away.
  const hdrSection = header.closest('.shopify-section') || header.parentElement;
  const annoEl = hdrSection ? hdrSection.previousElementSibling : null;
  const annoH = () => (annoEl ? annoEl.offsetHeight : 0);

  let ticking = false;

  // Condense + pin, always visible (best-UX call for the 45-75 buyer: the nav
  // never disappears — cart + menu always one tap away). `reduce` still governs
  // whether the condense transition animates (CSS handles that).
  void reduce;
  function onScroll() {
    const y = getY();
    header.style.setProperty('--vfw-hdr-top', Math.max(0, annoH() - y) + 'px');
    header.classList.toggle('is-condensed', y > 40);
    // Scrolling the page is an unambiguous "done with the nav", and scrolling
    // with a stationary pointer fires no mouseleave, so nothing else would
    // close the panel. Now that .is-open is the only authority (see the
    // vfw-nav-js hand-off above), this genuinely closes instead of stranding a
    // :hover-held slab over the page — which is exactly what James hit on
    // 2026-08-04: dwell in the panel, scroll, and it rode down the page eating
    // clicks. Threshold, not any-delta, so a resting trackpad can't twitch the
    // menu shut. Re-opening needs the pointer to leave and return, the same as
    // after Escape.
    if (
      header.querySelector('[data-nav-item].is-open') &&
      Math.abs(y - openedAtY) > SCROLL_DISMISS
    ) {
      closeNow();
    }
    ticking = false;
  }
  function requestTick() {
    if (!ticking) {
      window.requestAnimationFrame(onScroll);
      ticking = true;
    }
  }
  window.addEventListener('scroll', requestTick, { passive: true });
  if (scroller) scroller.addEventListener('scroll', requestTick, { passive: true });
  window.addEventListener('resize', requestTick, { passive: true });
  onScroll(); // set the initial offset
})();

/* ── Scroll memory v2 (2026-07-30) ──────────────────────────────
   v1 re-applied the position in a settle loop for ~1.2s after pageshow —
   which MOVED the page under an impatient thumb and caused James's
   "clicked something and ended up on a random page". v2: restore in a
   rAF loop that stops the instant the user touches, scrolls, or presses
   anything, and hard-stops at 2.5s. Both scrollers driven (desktop =
   .page-wrapper, phones = window). */
(function () {
  'use strict';
  var KEY = 'vfw-scroll:' + location.pathname;
  function readPos() {
    var pw = document.querySelector('.page-wrapper');
    return Math.max(window.scrollY || 0, pw ? pw.scrollTop : 0);
  }
  function writePos(v) {
    // base.css sets scroll-behavior: smooth — a plain scrollTo ANIMATES,
    // which read as a slow crawl before the final jump. Instant, always.
    var pw = document.querySelector('.page-wrapper');
    try { window.scrollTo({ top: v, left: 0, behavior: 'instant' }); }
    catch (e) { window.scrollTo(0, v); }
    if (pw) {
      var prev = pw.style.scrollBehavior;
      pw.style.scrollBehavior = 'auto';
      pw.scrollTop = v;
      pw.style.scrollBehavior = prev;
    }
  }
  window.addEventListener('pagehide', function () {
    try { sessionStorage.setItem(KEY, String(readPos())); } catch (e) {}
  });
  window.addEventListener('pageshow', function () {
    var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    if (!nav || nav.type !== 'back_forward') return;
    var v = 0;
    try { v = parseInt(sessionStorage.getItem(KEY) || '0', 10); } catch (e) {}
    if (v < 100) return;
    var cancelled = false;
    var cancel = function () { cancelled = true; };
    ['touchstart', 'wheel', 'pointerdown', 'keydown'].forEach(function (ev) {
      window.addEventListener(ev, cancel, { once: true, passive: true, capture: true });
    });
    var until = Date.now() + 2500;
    (function tick() {
      if (cancelled || Date.now() > until) return;
      if (Math.abs(readPos() - v) > 40) writePos(v);
      requestAnimationFrame(tick);
    })();
  });
})();
